import React from "react";
import { TruckDetailDialog } from "./FleetUtilizationCard.jsx";
import { dispatchUrl } from "./geotab/geotabLinks.js";
import { isLowUtilizationDay, LOW_UTILIZATION_THRESHOLD_PCT } from "../utils/fleetUtilizationData.js";
import { resolveComparisonLabel, resolveMetricDelta } from "../utils/dashboardComparison.js";

const PERCENT = new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 1 });
const dayMonth = (day) => `${day.dispatchDate.slice(8, 10)}/${day.dispatchDate.slice(5, 7)}`;
const plural = (count, word) => `${count} ${word}${count > 1 ? "s" : ""}`;
// Severity orders the list: points to correct first, then information, then what is fine.
const SEVERITY = { warning: 2, info: 1, ok: 0 };

function WatchItem({ tone, title, detail, action }) {
  return <li className="dashboard-watch-item">
    <span className="dashboard-watch-mark rounded-none" data-tone={tone} aria-hidden="true" />
    <div className="min-w-0">
      <p className="dashboard-watch-title">{title}</p>
      {detail ? <p className="dashboard-watch-detail">{detail}</p> : null}
      {action}
    </div>
  </li>;
}

export default function DashboardWatchList({ period, summary, previousSummary = null }) {
  const [dialogDay, setDialogDay] = React.useState(null);
  const days = (period.days || []).filter((day) => day.hasDispatch);
  const lowDays = days.filter(isLowUtilizationDay).reverse();
  const lowLoadDelta = previousSummary ? resolveMetricDelta(summary.lowLoadDistancePct, previousSummary.lowLoadDistancePct, { unit: "pts", higherIsBetter: false }) : null;
  const lastDay = days.at(-1);
  const lowestPeak = days.filter((day) => day.avgPeakLoadFillRatePct != null)
    .reduce((lowest, day) => (!lowest || day.avgPeakLoadFillRatePct < lowest.avgPeakLoadFillRatePct ? day : lowest), null);
  const link = (label, props) => <a className="dashboard-watch-link" {...props}>{label} →</a>;
  const items = [
    lowDays.length ? {
      key: "low", tone: "warning",
      title: `${plural(lowDays.length, "journée")} sous ${LOW_UTILIZATION_THRESHOLD_PCT} % d’utilisation`,
      detail: `${lowDays.slice(0, 5).map(dayMonth).join(", ")}${lowDays.length > 5 ? " …" : ""}`,
      action: <button type="button" className="dashboard-watch-link" onClick={() => setDialogDay(lowDays[0])}>Voir les camions →</button>,
    } : {
      key: "low", tone: "ok", title: `Aucune journée sous ${LOW_UTILIZATION_THRESHOLD_PCT} % d’utilisation`,
      detail: days.length ? "Toutes les journées de la période dépassent le seuil." : "Aucun dispatch sur la période.",
    },
    {
      key: "low-load", tone: lowLoadDelta && lowLoadDelta.value > 0 ? "warning" : "info",
      title: `Km à vide / faible charge : ${summary.lowLoadDistancePctLabel}`,
      detail: lowLoadDelta ? `${lowLoadDelta.label} ${resolveComparisonLabel(period, previousSummary.dispatchDayCount)}. Vérifier les retours à vide et les regroupements.`
        : "Pas de période précédente à comparer.",
      action: lastDay ? link("Ouvrir le Dispatch", { href: dispatchUrl(lastDay.dispatchDate.slice(0, 10)) }) : null,
    },
    ...(lowestPeak ? [{
      key: "peak", tone: "info",
      title: `Remplissage le plus bas : ${PERCENT.format(lowestPeak.avgPeakLoadFillRatePct)} % le ${dayMonth(lowestPeak)}`,
      detail: `${plural(lowestPeak.usedTruckCount, "camion")} utilisé${lowestPeak.usedTruckCount > 1 ? "s" : ""} sur ${lowestPeak.activeTruckCount} actifs ce jour-là.`,
      action: link("Voir le dispatch", { href: dispatchUrl(lowestPeak.dispatchDate.slice(0, 10)) }),
    }] : []),
  ].sort((left, right) => SEVERITY[right.tone] - SEVERITY[left.tone]);
  const warnings = items.filter((item) => item.tone === "warning").length;
  return <section className="dashboard-card dashboard-watch rounded-[4px]" aria-label="À surveiller">
    <header className="dashboard-card-header">
      <div><h2>À surveiller</h2><p className="dashboard-watch-count" data-tone={warnings ? "warning" : "ok"}>{warnings ? `${plural(warnings, "point")} à vérifier` : "Rien à vérifier"}</p></div>
      <p className="dashboard-watch-scope">{period.label}</p>
    </header>
    <ul className="dashboard-watch-list">{items.map(({ key, ...item }) => <WatchItem key={key} {...item} />)}</ul>
    {dialogDay ? <TruckDetailDialog day={dialogDay} onClose={() => setDialogDay(null)} /> : null}
  </section>;
}
